import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiBook, FiUsers, FiGlobe, FiPlay, FiAward, FiClock, FiList } = FiIcons;

const Introduction = ({ updateProgress, setCurrentSection }) => {
  useEffect(() => {
    setCurrentSection('introduction');
    updateProgress('introduction');
  }, []);

  const stats = [
    { label: 'Time Spent', value: '2.5h', icon: FiClock, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'Badges', value: '1', icon: FiAward, color: 'text-amber-600', bg: 'bg-amber-100' },
    { label: 'Modules', value: '7', icon: FiBook, color: 'text-purple-600', bg: 'bg-purple-100' },
  ];

  const modules = [
    { title: "1. Nouns & Gender", desc: "Master the basics of Amharic nouns, gender distinctions, and pluralization.", icon: FiBook, path: "/nouns", color: "bg-green-500" },
    { title: "2. Pronouns & Respect", desc: "Learn pronouns that reflect social hierarchy and relationships.", icon: FiUsers, path: "/pronouns", color: "bg-blue-500" },
    { title: "3. Cultural Context", desc: "Explore how grammar applies to family, church, and arbitration.", icon: FiGlobe, path: "/cultural", color: "bg-amber-500" },
    { title: "4. Interactive Practice", desc: "Role-play scenarios and test your knowledge.", icon: FiPlay, path: "/exercises", color: "bg-rose-500" }
  ];

  return (
    <div className="space-y-8 fade-in relative">
      {/* Hero Section */}
      <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-green-900 via-green-800 to-emerald-800 text-white shadow-2xl border border-green-700/30">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 p-8 opacity-10 transform translate-x-10 -translate-y-10">
          <SafeIcon icon={FiGlobe} className="text-9xl" />
        </div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="relative z-10 p-8 md:p-12 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-yellow-300 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>
            Welcome to BKAmharic
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
            Master the Social Art of <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-300">
              Amharic Grammar
            </span>
          </h1>
          
          <p className="text-green-50 text-lg md:text-xl mb-10 leading-relaxed max-w-2xl font-light">
            Beyond just words, learn how language connects people. Discover the unique "We" vs "I" dynamic and the art of respect in Ethiopian culture.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Link 
              to="/nouns" 
              className="group relative px-8 py-4 rounded-2xl bg-white text-green-900 font-bold text-lg shadow-[0_4px_14px_0_rgba(255,255,255,0.39)] hover:shadow-[0_6px_20px_rgba(255,255,255,0.23)] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3 overflow-hidden"
            >
              <span className="relative z-10">Start Learning</span>
              <SafeIcon icon={FiArrowRight} className="relative z-10 text-xl group-hover:translate-x-1 transition-transform" />
              <div className="absolute inset-0 bg-green-50 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
            </Link>
            
            <Link 
              to="/syllabus"
              className="group px-8 py-4 rounded-2xl bg-green-800/30 backdrop-blur-sm border border-white/30 text-white font-bold text-lg hover:bg-white/10 hover:border-white/60 transition-all duration-300 flex items-center justify-center gap-3"
            >
              <SafeIcon icon={FiList} className="text-xl opacity-80 group-hover:opacity-100" />
              <span>View Syllabus</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex items-center gap-5">
            <div className={`w-14 h-14 rounded-2xl ${stat.bg} ${stat.color} flex items-center justify-center text-2xl shadow-sm`}>
              <SafeIcon icon={stat.icon} />
            </div>
            <div>
              <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">{stat.label}</div>
              <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Learning Modules */}
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Your Learning Path</h2>
            <p className="text-gray-500 mt-1">Follow the structured modules to master the language</p>
          </div>
          <span className="hidden md:inline-flex px-4 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm font-semibold">
            4 Core Modules
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {modules.map((mod, idx) => (
            <Link 
              key={idx} 
              to={mod.path}
              className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
            >
              <div className={`absolute top-0 left-0 w-1.5 h-full ${mod.color}`}></div>
              <div className="flex justify-between items-start mb-4 pl-2">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg ${mod.color} group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={mod.icon} className="text-2xl" />
                </div>
                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-green-50 group-hover:text-green-600 transition-colors">
                  <SafeIcon icon={FiArrowRight} className="text-lg group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
              <div className="pl-2">
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-green-700 transition-colors">
                  {mod.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {mod.desc}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Introduction;